import { Component } from '@angular/core';

interface Faq {
  question: string;
  answer: string;
}

@Component({
  selector: 'app-faq',
  templateUrl: './faq.component.html',
  styleUrl: './faq.component.scss'
})
export class FaqComponent {

  categories = [
    'Orders',
    'Delivery',
    'Payments',
    'Returns',
    'Refunds',
    'Account',
    'Sellers',
    'Rewards',
    'Security'
  ];

  selectedCategory = 'Orders';
  openedFaq: number | null = 0;

  private faqsByCategory: Record<string, Faq[]> = {
    Orders: [
      {
        question: 'How can I place an order on VinMarts?',
        answer:
          'You can place an order by adding products to your cart and proceeding to checkout. Fill in your delivery details, choose a payment method, and click on Place Order.'
      },
      {
        question: 'How can I track my order?',
        answer:
          'You can track your order from the My Orders section of your account.'
      },
      {
        question: 'What payment methods does VinMarts support?',
        answer:
          'VinMarts supports multiple secure payment options including cards, UPI and other available payment methods.'
      },
      {
        question: 'Can I cancel or modify my order after placing it?',
        answer:
          'Order cancellation or modification depends on the current status of your order.'
      },
      {
        question: 'What is VinMarts return policy?',
        answer:
          'Eligible products can be returned according to the applicable return policy.'
      },
      {
        question: 'When will I receive my refund?',
        answer:
          'Refund processing time depends on the payment method and bank.'
      },
      {
        question: 'How can I use my VinMarts rewards?',
        answer:
          'Your available rewards can be used according to the reward terms during checkout.'
      },
      {
        question: 'How can I contact the seller?',
        answer:
          'You can contact the seller through the available seller communication options.'
      }
    ],
    Delivery: [
      {
        question: 'How can I track my delivery?',
        answer:
          'Open My Orders and select your order to view its latest delivery status and tracking details.'
      },
      {
        question: 'How long does delivery take?',
        answer:
          'The estimated delivery date is shown on the product page and in your order details. Delivery time can vary by location.'
      },
      {
        question: 'Can I change my delivery address?',
        answer:
          'You can update the delivery address from your order details if the order has not yet been dispatched.'
      },
      {
        question: 'Can I choose a delivery time slot?',
        answer:
          'Available delivery options and time slots are shown during checkout for your address.'
      },
      {
        question: 'What should I do if my delivery is delayed?',
        answer:
          'Check the latest status in My Orders. If the updated delivery estimate has passed, contact our support team with your order details.'
      },
      {
        question: 'Can someone else receive my order?',
        answer:
          'Yes. Someone at the delivery address can receive the order, subject to any verification required for that delivery.'
      }
    ],
    Payments: [
      {
        question: 'What payment methods does VinMarts support?',
        answer:
          'VinMarts supports multiple secure payment options, including cards, UPI and other payment methods shown at checkout.'
      },
      {
        question: 'When will my payment be charged?',
        answer:
          'Your selected payment method is charged when you place the order. Check your order details for the payment status.'
      },
      {
        question: 'What should I do if my payment fails?',
        answer:
          'Check your bank or payment app for the transaction status, then try again with an available payment method if the order was not placed.'
      },
      {
        question: 'How can I check whether my payment was successful?',
        answer:
          'Check the payment status in My Orders. If money was deducted but the order is not listed, contact support with your transaction details.'
      },
      {
        question: 'Can I change the payment method after placing an order?',
        answer:
          'Payment methods generally cannot be changed after an order is placed. Check your order details for the options available for that order.'
      },
      {
        question: 'Is cash on delivery available?',
        answer:
          'Available payment methods, including cash on delivery where offered, are displayed during checkout for your order.'
      }
    ],
    Returns: [
      {
        question: 'What is VinMarts return policy?',
        answer:
          'Eligible products can be returned according to the return policy shown on the product page and in your order details.'
      },
      {
        question: 'How can I return a product?',
        answer:
          'Go to My Orders, select the eligible product and choose the return option to follow the instructions.'
      },
      {
        question: 'How can I check my return status?',
        answer:
          'You can check the latest return status from the relevant order in My Orders.'
      },
      {
        question: 'How long do I have to return a product?',
        answer:
          'The return window can vary by product. Check the product page or your order details for the applicable deadline.'
      },
      {
        question: 'Do I need the original packaging to return a product?',
        answer:
          'Return requirements can vary by product. Follow the instructions shown when you start a return for your order.'
      },
      {
        question: 'Can I return only one item from my order?',
        answer:
          'If the item is eligible, select that item from My Orders and follow the return instructions.'
      }
    ],
    Refunds: [
      {
        question: 'When will I receive my refund?',
        answer:
          'Refund processing time depends on the payment method and your bank. You can check the refund status in your order details.'
      },
      {
        question: 'Where will my refund be credited?',
        answer:
          'Refunds are sent to the original payment method or handled as shown in the refund details for your order.'
      },
      {
        question: 'What should I do if my refund is delayed?',
        answer:
          'Check the refund status in My Orders first. If the stated processing time has passed, contact our support team with your order details.'
      },
      {
        question: 'How can I check my refund status?',
        answer:
          'Open the relevant order in My Orders to see the latest refund status and available details.'
      },
      {
        question: 'Will I receive a confirmation when my refund is processed?',
        answer:
          'Refund updates are shown in your order details. Check your registered contact details for any related notifications.'
      },
      {
        question: 'Can I request a refund for a cancelled order?',
        answer:
          'If payment was collected for a cancelled order, check the refund status in My Orders. Contact support if you need help.'
      }
    ],
    Account: [
      {
        question: 'How can I update my account details?',
        answer:
          'Sign in to VinMarts, open your profile and edit the account details you want to update.'
      },
      {
        question: 'How can I reset my password?',
        answer:
          'Choose the Forgot Password option on the sign-in page and follow the instructions to reset your password.'
      },
      {
        question: 'How can I manage my saved addresses?',
        answer:
          'Open your account profile to add, edit or remove saved delivery addresses.'
      },
      {
        question: 'How can I update my email address or phone number?',
        answer:
          'Open your profile settings and edit the contact details available there. Complete any verification steps shown.'
      },
      {
        question: 'How do I sign out of my account?',
        answer:
          'Open your account menu and select the sign-out option.'
      },
      {
        question: 'What should I do if I cannot sign in?',
        answer:
          'Check your sign-in details and use Forgot Password if needed. Contact support if you still cannot access your account.'
      }
    ],
    Sellers: [
      {
        question: 'How can I contact a seller?',
        answer:
          'Open the product or order details and use the seller communication options available there.'
      },
      {
        question: 'How can I become a VinMarts seller?',
        answer:
          'Select Become a Seller on VinMarts and follow the steps to submit your seller registration details.'
      },
      {
        question: 'Where can I find seller information?',
        answer:
          'Seller information is available on the product page and in the details for your order.'
      },
      {
        question: 'How can I check a seller’s ratings?',
        answer:
          'Visit the product page to view the seller information and any ratings or feedback shown there.'
      },
      {
        question: 'Who should I contact about an order from a seller?',
        answer:
          'Check your order details for seller communication options. You can also contact VinMarts support for help with your order.'
      },
      {
        question: 'How can I update my seller account details?',
        answer:
          'Sign in to your seller account and use the available profile or account settings to manage your details.'
      }
    ],
    Rewards: [
      {
        question: 'How can I use my VinMarts rewards?',
        answer:
          'Your available rewards can be used according to the reward terms shown during checkout.'
      },
      {
        question: 'Where can I check my rewards balance?',
        answer:
          'Your current rewards balance is available in the Rewards section of your account.'
      },
      {
        question: 'Why can’t I use a reward on my order?',
        answer:
          'Rewards may have eligibility requirements or expiry dates. Check the reward terms and your order total.'
      },
      {
        question: 'Do my rewards expire?',
        answer:
          'Some rewards may have an expiry date. Check the terms shown with each reward in your account.'
      },
      {
        question: 'Can I use more than one reward on an order?',
        answer:
          'Reward combinations depend on the applicable terms. Any available rewards will be shown during checkout.'
      },
      {
        question: 'Why has my rewards balance changed?',
        answer:
          'Your balance can change when rewards are earned, used or expire. Review the Rewards section of your account for details.'
      }
    ],
    Security: [
      {
        question: 'How does VinMarts protect my account?',
        answer:
          'Use a strong, unique password and keep your sign-in details private. Contact support if you notice unusual activity.'
      },
      {
        question: 'Is it safe to make payments on VinMarts?',
        answer:
          'Use the payment options provided at checkout and never share your password, OTP or payment PIN with anyone.'
      },
      {
        question: 'What should I do if I notice suspicious account activity?',
        answer:
          'Change your password as soon as possible and contact the VinMarts support team for help securing your account.'
      },
      {
        question: 'Will VinMarts ask me to share my OTP or password?',
        answer:
          'Never share your password, OTP or payment PIN with anyone, including someone claiming to be from support.'
      },
      {
        question: 'How can I create a stronger password?',
        answer:
          'Use a long, unique password that you do not reuse on other websites, and keep it private.'
      },
      {
        question: 'What should I do if I receive a suspicious message?',
        answer:
          'Do not share personal or payment details through suspicious links. Contact VinMarts support if you are unsure whether a message is genuine.'
      }
    ]
  };

  helpLinks = [
    {
      icon: '🚚',
      title: 'Shipping & Delivery'
    },
    {
      icon: '↩️',
      title: 'Returns & Refunds'
    },
    {
      icon: '💳',
      title: 'Payments'
    },
    {
      icon: '👤',
      title: 'Account & Profile'
    },
    {
      icon: '▣',
      title: 'Terms & Conditions'
    },
    {
      icon: '🛡️',
      title: 'Privacy Policy'
    }
  ];

  get faqs(): Faq[] {
    return this.faqsByCategory[this.selectedCategory] || [];
  }

  selectCategory(category: string): void {
    this.selectedCategory = category;
    this.openedFaq = 0;
  }

  toggleFaq(index: number): void {
    this.openedFaq = this.openedFaq === index ? null : index;
  }
}
